import { useMask, Mask } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useController } from "@react-three/xr";
import React from "react";
import * as THREE from "three";
import { create } from "zustand";

const pos = new THREE.Vector3();
const dir = new THREE.Vector3();

const posR = new THREE.Vector3();
const dirR = new THREE.Vector3();

const CursorNew = () => {
  const circleRef = React.useRef<THREE.Group>(null);
  const circleRefR = React.useRef<THREE.Group>(null);

  const cursor = useCursorShrinkingStore();

  const maskRef = React.useRef<THREE.Mesh>(null);
  const maskRefR = React.useRef<THREE.Mesh>(null);

  const stencil = useMask(1, true);
  const stencilR = useMask(2, true);

  const leftController = useController("left");
  const rightController = useController("right");
  const gazeController = useController("none");

  const caster = useThree((state) => {
    return state.raycaster;
  });

  const casterR = useThree((state) => {
    return state.raycaster;
  });

  useFrame((state) => {
    circleRef.current?.lookAt(state.camera.position);
    circleRefR.current?.lookAt(state.camera.position);

    //
    //QUEST
    if (leftController || rightController) {
      const lC = state.gl.xr.getController(0);
      const rC = state.gl.xr.getController(1);
      if (circleRef.current) circleRef.current.visible = lC?.visible;
      if (circleRefR.current) circleRefR.current.visible = rC?.visible;
      if (lC.visible) {
        lC.getWorldDirection(dir);
        lC.getWorldPosition(pos);
        const leftCirclePos = dir.multiplyScalar(-200).add(pos);
        circleRef.current?.position.copy(leftCirclePos);

        lC?.getWorldDirection(rayDir);
        caster.set(
          pos,
          rayDir?.multiplyScalar(-1) || new THREE.Vector3(0, 0, 0)
        );
        caster.camera = state.camera;
        try {
          if (
            caster
              .intersectObjects(state.scene.children)
              ?.filter(
                (interceptions) =>
                  interceptions.object.name === "hotspot" ||
                  interceptions.object.name === "answer" ||
                  interceptions.object.name === "close"
              ).length > 0
          ) {
            circleRef.current?.scale.lerp(hoverCircleScale2, 0.25);
            maskRef.current?.scale.lerp(hoverMaskScale, 0.25);
          } else {
            circleRef.current?.scale.lerp(initialCircleScale2, 0.1);
            maskRef.current?.scale.lerp(initialMaskScale, 0.1);
          }
        } catch {}
      }

      if (rC.visible) {
        rC.getWorldDirection(dirR);
        rC.getWorldPosition(posR);

        const rightCirclePos = dirR.multiplyScalar(-200).add(posR);

        circleRefR.current?.position.copy(rightCirclePos);

        //ANIMATE

        rC?.getWorldDirection(rayDirR);

        casterR.set(
          posR,
          rayDirR?.multiplyScalar(-1) || new THREE.Vector3(0, 0, 0)
        );
        casterR.camera = state.camera;

        try {
          if (
            casterR
              .intersectObjects(state.scene.children)
              ?.filter(
                (interceptions) =>
                  interceptions.object.name === "hotspot" ||
                  interceptions.object.name === "answer" ||
                  interceptions.object.name === "close"
              ).length > 0
          ) {
            circleRefR.current?.scale.lerp(hoverCircleScale2, 0.25);
            maskRefR.current?.scale.lerp(hoverMaskScale, 0.25);
          } else {
            circleRefR.current?.scale.lerp(initialCircleScale2, 0.1);
            maskRefR.current?.scale.lerp(initialMaskScale, 0.1);
          }
        } catch {}
      }
    }

    //
    //CARDBOARD
    else if (gazeController) {
      if (circleRef.current) circleRef.current.visible = true;
      if (circleRefR.current) circleRefR.current.visible = false;

      const caster = state.raycaster;

      state.camera.getWorldDirection(dir);
      state.camera.getWorldPosition(pos);

      caster.set(pos, dir);

      if (
        caster
          .intersectObjects(state.scene.children)
          ?.filter(
            (interceptions) =>
              interceptions.object.name === "hotspot" ||
              interceptions.object.name === "answer" ||
              interceptions.object.name === "close"
          ).length > 0
      ) {
        circleRef.current?.scale.lerp(hoverCircleScale, 0.25);

        if (!cursor.shrinking) {
          maskRef.current?.scale.lerp(new THREE.Vector3(1, 1, 1), 0.2);
        } else maskRef.current?.scale.lerp(new THREE.Vector3(0, 0, 0), 0.02);
      } else {
        circleRef.current?.scale.lerp(initialCircleScale, 0.4);
        maskRef.current?.scale.lerp(new THREE.Vector3(0, 0, 0), 0.4);
      }

      const gazeCursorPos = dir.multiplyScalar(5).add(pos);

      circleRef.current?.position.copy(gazeCursorPos);
    }
  });

  return (
    <>
      {rightController && (
        <group ref={circleRefR}>
          <mesh renderOrder={4} name="cursor">
            <circleGeometry args={[0.05, 32]} />
            <meshBasicMaterial {...stencilR} transparent depthTest={false} />
          </mesh>
          <Mask name="cursor mask" id={2} scale={0} ref={maskRefR}>
            <circleGeometry args={[0.04, 32]} />
          </Mask>
        </group>
      )}

      <group ref={circleRef}>
        <mesh renderOrder={4} name="cursor">
          <circleGeometry args={[0.05, 32]} />
          <meshBasicMaterial {...stencil} transparent depthTest={false} />
        </mesh>
        <Mask name="cursor mask" id={1} scale={0} ref={maskRef}>
          <circleGeometry args={[0.04, 32]} />
        </Mask>
      </group>
    </>
  );
};

export default CursorNew;

type cursorShrinkingStoreType = {
  shrinking: boolean;
  setShrinking: (bool: boolean) => void;
};

export const useCursorShrinkingStore = create<cursorShrinkingStoreType>()(
  (set) => ({
    shrinking: false,
    setShrinking: (bool: boolean) => set({ shrinking: bool }),
  })
);

const rayDir = new THREE.Vector3();
const rayDirR = new THREE.Vector3();

const initialCircleScale = new THREE.Vector3(0.8, 0.8, 0.8);
const hoverCircleScale = new THREE.Vector3(1.7, 1.7, 1.7);

const initialCircleScale2 = new THREE.Vector3(10, 10, 10);
const hoverCircleScale2 = new THREE.Vector3(30, 30, 30);

const initialMaskScale = new THREE.Vector3(0, 0, 0);
const hoverMaskScale = new THREE.Vector3(1, 1, 1);
